import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Box,
  Button,
  Callout,
  Checkbox,
  Input,
  Radio,
  Sheet,
  toast,
} from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { Approval } from 'constants/General'
import { useCreateAccountUserMutation } from 'hooks/mutations/useCreateAccountUserMutation'
import { useUpdateAccountUserMutation } from 'hooks/mutations/useUpdateAccountUserMutation'
import { UserGroups } from 'pages/TeamMembers/const'
import { AccountUser, AccountUserGroup, UserGroupType } from 'types/accounts'
import { FormFieldsAccountUser } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { validEmailRegex } from 'utils/email'
import track, { Amp } from 'utils/tracker'

type TeamMemberEditorProps = {
  accountUser?: AccountUser
  userGroups?: AccountUserGroup[]
  action: 'EDIT' | 'CREATE'
  canEdit?: boolean
  canManageApprover?: boolean
  handleCloseModal: () => void
  getValues: () => void
}

export const TeamMemberEditor = ({
  accountUser,
  userGroups,
  action,
  canEdit,
  canManageApprover,
  handleCloseModal,
  getValues,
}: TeamMemberEditorProps) => {
  const { t } = useTranslation()

  const existingUser = accountUser?.groups?.[0]?.id?.toString() || ''

  const [errors, setErrors] = useState(null)
  const [formData, setFormData] = useState({
    emailAddress: accountUser?.emailAddress || '',
    groups: accountUser?.groups || [],
    ...(action === 'CREATE' && canManageApprover && { approver: false }),
  })
  const [errorMessage, setErrorMessage] = useState(null)
  const [selectedRadioOption, setSelectedRadioOption] = useState<string>(existingUser)

  const { mutate: mutateCreateUser, isPending: isLoadingCreateUser } =
    useCreateAccountUserMutation()
  const { mutate: mutateUpdateUser, isPending: isLoadingUpdateUser } =
    useUpdateAccountUserMutation()

  useEffect(() => {
    if (!existingUser) {
      Amp.track(AmplitudeEvent.ADD_TEAM_MEMBER_INITIATE, {
        category: AmplitudeEventCategory.APPLICATION,
        action: AmplitudeEventAction.CLICK,
      })
    }
    if (!!existingUser) {
      Amp.track(AmplitudeEvent.EDIT_TEAM_MEMBER_INITIATE, {
        category: AmplitudeEventCategory.APPLICATION,
        action: AmplitudeEventAction.CLICK,
      })
    }
  }, [])

  const mapTranslationsSubText = (groupName: UserGroupType) => {
    switch (UserGroups[groupName]) {
      case UserGroups['Workspace Owner']:
        return t('teamMember.workspaceOwnerSubText')
      case UserGroups.Admin:
        return t('teamMember.adminSubText')
      case UserGroups.Collaborator:
        return t('teamMember.collaboratorSubText')
      case UserGroups['Pay-In Only']:
      case UserGroups['Pay-In Only ']:
        return t('teamMember.payInSubText')
      case UserGroups.Viewer:
        return t('teamMember.viewerSubText')
      case UserGroups.Compliance:
        return t('teamMember.complianceSubText')
      case UserGroups['Compliance-Plus']:
        return t('teamMember.compliancePlusSubText')
      default:
        return ''
    }
  }

  const validate = () => {
    setErrors(null)
    const tempErrorList = {}

    if (formData.emailAddress?.length < 1) {
      tempErrorList['emailAddress'] = {
        status: true,
        message: t('emailRequired'),
      }
    } else {
      const validEmail = new RegExp(validEmailRegex)
      if (!validEmail.test(formData.emailAddress)) {
        tempErrorList['emailAddress'] = {
          status: true,
          message: t('validEmailRequired'),
        }
      }
    }

    if (formData.groups?.length < 1) {
      tempErrorList['groups'] = {
        status: true,
      }
    }

    let hasErrors = false
    for (const property in tempErrorList) {
      if (tempErrorList[property].status === true) {
        hasErrors = true
      }
    }

    setErrors(tempErrorList)

    return hasErrors
  }

  const onSubmit = async () => {
    const hasErrors = validate()
    if (!hasErrors) {
      saveUser(formData)
    }
  }

  const onCheckboxChange = (id: number) => {
    const selectedAccountGroupItem = formData?.groups?.find(group => group?.id === id)
    let newGroups: AccountUserGroup[]

    if (selectedAccountGroupItem) {
      newGroups = formData?.groups?.filter(group => group?.id !== id)
    } else {
      const newItem = userGroups?.find(item => item?.id === id)
      newGroups = [...formData.groups, newItem]
    }

    setFormData({ ...formData, groups: newGroups })
  }

  const onEmailInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, emailAddress: e.target.value })
  }

  const onRadioChange = (id: number | React.SetStateAction<string>) => {
    const selectedUserGroup = Array(1).fill(userGroups?.find(group => group?.id == id))

    setFormData({
      ...formData,
      groups: selectedUserGroup,
    })
    setSelectedRadioOption(id?.toString())
  }

  const radioOptions = userGroups
    ?.filter(userGroup => userGroup?.name !== Approval.ACCOUNT_TRANSACTION_APPROVER)
    ?.map(group => {
      return {
        label: group?.name,
        value: `${group?.id}`,
        secondaryLabel: mapTranslationsSubText(group?.name),
      }
    })

  const onApproverCheckboxChange = () => {
    setFormData({
      ...formData,
      approver: !formData.approver,
    })
  }

  const handleSuccess = (isCreate: boolean) => {
    handleCloseModal()
    setErrorMessage(null)
    getValues()

    toast({
      variant: 'success',
      header: isCreate ? t('teamMember.addSuccessHeading') : t('teamMember.editSuccessHeading'),
      body: isCreate ? t('teamMember.addSuccessSubheading') : t('teamMember.editSuccessSubheading'),
    })
  }

  const createNewUser = (formData: FormFieldsAccountUser) => {
    mutateCreateUser(formData, {
      onSuccess: () => {
        handleSuccess(true)
        track.Amp.track(AmplitudeEvent.EOF_ADD_TEAM_MEMBER_SUCCESS, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.VIEW,
        })
      },
      onError: (error: Error) => {
        logSentryError(`Error from TeamMemberEditor - createNewUser`, error)
        setErrorMessage(error?.message || t('defaultAddTeamMemberError'))
      },
    })
  }

  const updateExistingUser = (formData: FormFieldsAccountUser) => {
    mutateUpdateUser(
      { userID: accountUser?.id, formData },
      {
        onSuccess: () => {
          handleSuccess(false)
          track.Amp.track(AmplitudeEvent.EOF_EDIT_TEAM_MEMBER_SUCCESS, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.VIEW,
          })
        },
        onError: (error: Error) => {
          logSentryError(`Error from TeamMemberDelete - updateExistingUser`, error)
          setErrorMessage(error?.message || t('defaultEditTeamMemberError'))
        },
      }
    )
  }

  const saveUser = (formData: FormFieldsAccountUser) => {
    if (!!accountUser) {
      updateExistingUser(formData)
    } else {
      createNewUser(formData)
    }
  }

  return (
    <Sheet
      isOpen
      onClose={handleCloseModal}
      header={action === 'EDIT' ? t('editTeamMember') : t('createTeamMember')}
    >
      <Box flexCol gap={24}>
        <Input
          label={t('teamMemberEmailAddress')}
          placeholder={t('teamMemberEmailAddressPlaceholder')}
          name="emailAddress"
          testId="email-address"
          disabled={!!accountUser}
          onChange={onEmailInputChange}
          value={formData?.emailAddress}
          error={errors?.emailAddress?.status}
          errorText={errors?.emailAddress?.status && errors?.emailAddress?.message}
          required
        />
        <Box>
          <Radio
            name="groupOne"
            onChange={onRadioChange}
            options={radioOptions}
            value={selectedRadioOption}
            horizontal={false}
            border={false}
          />
          {action === 'CREATE' && canManageApprover && (
            <Box flex paddingX={12} paddingY={16}>
              <Checkbox
                id="team-member-approver-checkbox"
                name="team-member-approver-checkbox"
                testId="team-member-approver-checkbox"
                checked={formData.approver}
                label={t('teamMember.makeUserAnApprover')}
                onCheckedChange={onApproverCheckboxChange}
              >
                {t('teamMember.makeUserAnApproverDescription')}
              </Checkbox>
            </Box>
          )}
        </Box>
        <Callout variant="info">
          <span>
            {t('teamMember.permissionsCalloutPartOne')}{' '}
            <Link className="underline" to={process.env.ROLES_FAQ}>
              {t('teamMember.permissionsCalloutPartTwo')}
            </Link>{' '}
            {t('teamMember.permissionsCalloutPartThree')}
          </span>
        </Callout>

        {errors?.groups?.status && <Box className="mt-1 text-red-25">{t('selectOneOption')}</Box>}
        {!!errorMessage && <Box className="mt-1 text-red-25 error-text">{errorMessage}</Box>}

        <Box flex justify="between">
          <Button onClick={handleCloseModal} variant="secondary">
            {t('cancel')}
          </Button>
          <Button
            onClick={onSubmit}
            testId="submit-button"
            loading={isLoadingCreateUser || isLoadingUpdateUser}
          >
            {action === 'EDIT' ? t('save') : t('sendTeamMemberInvite')}
          </Button>
        </Box>
      </Box>
    </Sheet>
  )
}
